import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import { QuestionnaireService } from 'src/app/services/questionnaire/questionnaire.service';
import moment from 'moment-timezone';
import { AlertBannerService, AlertType } from "../../lib/alert-banner";
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import * as _ from 'lodash';
import {ConstantsService} from "../../services/constants/constants.service";
import { OrganisationService } from 'src/app/services/organisation/organisation.service';


export interface QuestionnaireData {
  id?: number;
  questions: any[]; // Replace `any` with a specific question type if available
  config?: {
    notes?: {
      note_to_candidate?: string;
      note_to_referee?: string;
    };
    min_referee?: number;
  };
  is_opt_in_question_enabled?: boolean;
  opt_in_question?: string;
  name?: string;
  survey_type?: string;
}

@Component({
  selector: 'app-create-reference-survey',
  templateUrl: './create-reference-survey.component.pug',
  styleUrls: ['./create-reference-survey.component.scss'],
  standalone: false
})
export class CreateReferenceSurveyComponent implements OnInit {
  @Input() isEditTitle = false;
  @Input() configureOptInOnly:boolean = false;
  @Input() initialise = [];
  @Input() questionnaireTitle = '';
  @Input() max_questions_limit = 5;
  @Input() noteToCandidate = '';
  @Input() noteToReferee = '';
  @Input() isOptInQuestionEnabled: boolean = true;
  @Input() optInQuestion: string = ConstantsService.DEFAULT_OPT_IN_QUESTION
  @Input() backFromHowToPersonaliseModal: boolean = false;
  @Input() minReferee = ConstantsService.DEFAULT_REFEREE_COUNT;
  @Input() configureNotesToCandidateAndReferee: boolean = false;
  @Input() questionnaireData!: QuestionnaireData;

  public cancelClick: EventEmitter<any> = new EventEmitter();
  public skipClick: EventEmitter<any> = new EventEmitter();
  public nextClick: EventEmitter<any> = new EventEmitter();

  questionnaireId: string = '';
  // refer to OMNI-1455 in case of clarification needed about the regex
  titlePattern=new RegExp(/[^a-zA-Z0-9 _\-():',.\/&]+/);
  initialTitle: string = '';
  initialSelectedRefType: {} = {};
  invalidTitle = false;
  wrongPattern = false;
  metrics: any[] = [];
  selectedMetric={};
  disableButton = false;
  @Input() selectedRefType={
    "academic": false,
    "voluntary": false,
    "character": false
  }
  category_map = QuestionnaireService.category_map;
  initialIsOptInQuestionEnabled = true;
  initialOptInQuestion = ConstantsService.DEFAULT_OPT_IN_QUESTION
  defaultMinRefereeCount = ConstantsService.DEFAULT_MIN_REFEREE_COUNT;
  defaultMaxRefereeCount = ConstantsService.DEFAULT_MAX_REFEREE_COUNT;

   //errors
  referenceBelowExpectedCountError = false;
  referenceAboveExpectedCountError = false;
  referenceIncrementBtnDisabled = false;
  referenceDecrementBtnDisabled = false;
  referenceInputError = false;

  maxReferenceCountBannerDisplay = false;

  notesToCandidateValueError = false;
  notesToRefereeValueError = false;
  defaultNoteToCandidate = '';
  defaultNoteToReferee = '';
  isOptInLoaded = false;
  isNoteToCandidateAndRefereeLoaded = false;

  org_details = {
    notes: {
      note_to_candidate: '',
      note_to_referee: ''
    }
  };

  constructor(
      public bsModalRef: BsModalRef,
      private alertList: AlertBannerService,
      private questionnaireService: QuestionnaireService,
      private permissions: PermissionsService,
      private organisationService: OrganisationService,
  ) {
  }

  ngOnInit(): void {
    this.organisationService.getOrganisation().subscribe((orgDetails) => {
        this.defaultNoteToCandidate = orgDetails?.notes?.note_to_candidate || '';
        this.defaultNoteToReferee = orgDetails?.notes?.note_to_referee || '';
        }, error => {
        console.error('Failed to load organization details:', error);
        this.defaultNoteToCandidate = '';
        this.defaultNoteToReferee = '';
    });
    if( this.isEditTitle && this.configureNotesToCandidateAndReferee){

      this.questionnaireData.questions = this.mapQuestionCategories(this.questionnaireData.questions);
      this.isNoteToCandidateAndRefereeLoaded = true;

    }

    if (!this.backFromHowToPersonaliseModal) {
      if (!this.isEditTitle || !this.questionnaireTitle) {
        if (!this.configureOptInOnly) {
          this.generateQuestionnaireTitle();
        }
      }
      this.initialIsOptInQuestionEnabled = this.isOptInQuestionEnabled
      this.initialOptInQuestion = this.optInQuestion

      if (this.isEditTitle && this.configureOptInOnly) {
        this.questionnaireService.getQuestionnaire(this.questionnaireId).subscribe(
            res => {
              this.questionnaireData = res.data
              this.questionnaireData.questions = this.mapQuestionCategories(this.questionnaireData.questions);
              this.initialIsOptInQuestionEnabled = this.isOptInQuestionEnabled= res?.data?.is_opt_in_question_enabled;
              this.initialOptInQuestion = this.optInQuestion = res?.data?.opt_in_question;
              this.noteToCandidate = res.data?.config?.notes?.note_to_candidate || '';
              this.noteToReferee = res.data?.config?.notes?.note_to_referee || '';
              this.minReferee = res.data?.config?.min_referee || ConstantsService.DEFAULT_REFEREE_COUNT;
              this.isOptInLoaded = true;
              this.isNoteToCandidateAndRefereeLoaded = true;
            }
        )
      }
      else {
        this.isOptInLoaded = true;
      }
    } else {
      if (!this.isEditTitle || !this.questionnaireTitle) {
        this.generateQuestionnaireTitle();
      }
    }

    this.initialTitle = this.questionnaireTitle;
    this.initialSelectedRefType = {...this.selectedRefType};
    this.setValidations();
  }

  generateQuestionnaireTitle(): void{
    const today = new Date();
    const dateString = moment().startOf('day').format('ll');
    let name = $localize`Reference survey ` + dateString;
    this.verifyQuestionnaireTitle(name);
  }

  verifyQuestionnaireTitle(name): void{
    var regExp = /\(([^)]*)\)[^(]*$/;
    this.questionnaireService.getNewQuestionnaireTitle(name).subscribe(res=>{
      const next = res['questionnaires'].map(q=> {
        const matches = regExp.exec(q['name']);
        if (matches?.length==2 && !isNaN(Number(matches[1]))) return Number(matches[1]);
        return 0;
      }).sort()[res['questionnaires'].length-1]+1;
      if (next) this.questionnaireTitle = name + ` (${next})`;
      else this.questionnaireTitle = name;
      this.setValidations();
      return name;
    })
  }

  cancel(){
    this.cancelClick.emit({});
    this.bsModalRef.hide();
  }
  skip(){
    this.permissions.setNavCheck(true);
    this.skipClick.emit({
      'title': this.questionnaireTitle,
      'selectedRefType': this.selectedRefType,
      'isOptInQuestionEnabled': this.isOptInQuestionEnabled,
      'optInQuestion': this.optInQuestion,
      'minReferee': this.minReferee,
      'noteToCandidate': this.noteToCandidate,
      'noteToReferee': this.noteToReferee,
      'noteToCandidateChange': this.noteToCandidate?.trim() !== this.defaultNoteToCandidate?.trim(),
      'noteToRefereeChange': this.noteToReferee?.trim() !== this.defaultNoteToReferee?.trim(),
    });
    this.bsModalRef.hide();
  }

  handleAdditionalRefSurveySettings(){
    if (this.configureOptInOnly || this.configureNotesToCandidateAndReferee){
        this.questionnaireService.putQuestionnaire(
          this.questionnaireId,
          this.questionnaireData,
          'reference',
          false,
          this.isOptInQuestionEnabled,
          this.optInQuestion,
          this.minReferee,
          this.noteToCandidate,
          this.noteToReferee
        ).subscribe({
          next: () =>{
            this.alertList.append([{
                type: AlertType.SUCCESS,
                message: $localize`Success! Changes have been saved.`,
                keepOnRouteChange: true
              }]);
            this.bsModalRef?.hide?.()},
          error: err => {
            console.error('Error updating questionnaire:', err);
          }
        });
    } else {
        this.questionnaireService.validateQuestionnaireTitle(this.questionnaireTitle, this.questionnaireId).subscribe(res => {
        if (res.data.exists) {
          this.invalidTitle = true;
        } else {
          this.nextClick.emit({
            'title': this.questionnaireTitle,
            'selectedRefType': this.selectedRefType,
            'isOptInQuestionEnabled': this.isOptInQuestionEnabled,
            'optInQuestion': this.optInQuestion,
            'minReferee': this.minReferee,
            'noteToCandidate': this.noteToCandidate,
            'noteToReferee': this.noteToReferee,
            'noteToCandidateChange': this.noteToCandidate?.trim() !== this.defaultNoteToCandidate?.trim(),
            'noteToRefereeChange': this.noteToReferee?.trim() !== this.defaultNoteToReferee?.trim(),
          });
          this.bsModalRef.hide();
        }
        }, err => {});
      }
  }

  handleRITPAndOptInOnly() {
    if (this.configureOptInOnly){
        this.questionnaireService.putQuestionnaire(
          this.questionnaireId,
          this.questionnaireData,
          'reference',
          false,
          this.isOptInQuestionEnabled,
          this.optInQuestion,
        ).subscribe({
          next: () =>{
            this.alertList.append([{
                type: AlertType.SUCCESS,
                message: $localize`Success! Changes have been saved.`,
                keepOnRouteChange: true
              }]);
            this.bsModalRef?.hide?.()},
          error: err => {
            console.error('Error updating questionnaire:', err);
          }
        });
      }

      if (!this.configureOptInOnly){
        this.questionnaireService.validateQuestionnaireTitle(this.questionnaireTitle, this.questionnaireId).subscribe(res => {
        if (res.data.exists) {
          this.invalidTitle = true;
        } else {
          this.nextClick.emit({
            'title': this.questionnaireTitle,
            'selectedRefType': this.selectedRefType,
            'isOptInQuestionEnabled': this.isOptInQuestionEnabled,
            'optInQuestion': this.optInQuestion,
          });
        }
        this.bsModalRef.hide();
        }, err => {});
      }
  }

  next() {
    this.permissions.setNavCheck(true);
    this.handleAdditionalRefSurveySettings();
  }

  titleChange(title){
    this.invalidTitle=false;
    let patternMatch = this.titlePattern.test(title);
    this.wrongPattern = patternMatch;
    this.setValidations();
  }

  selectReferenceType(type){
    this.selectedRefType[type] =! this.selectedRefType[type];
    this.setValidations();
  }

  onToggleChange(value: boolean) {
    this.isOptInQuestionEnabled = value;
    this.setValidations();
  }

  onOptInQuestionChange(value: string) {
    this.optInQuestion = value;
    this.setValidations();
  }

  didNothingChange(): boolean {
    // If no survey settings have changed, this should return true
    const noTitleChange: boolean = this.initialTitle === this.questionnaireTitle;
    const noRefTypeChange: boolean = _.isEqual(this.initialSelectedRefType, this.selectedRefType);

    const noIsOptInQuestionEnabledChange: boolean = this.isOptInQuestionEnabled == this.initialIsOptInQuestionEnabled;
    // Do not allow opt-in question to be empty *if* opt-in is currently enabled
    const noOptInQuestionChange: boolean = (
        this.isOptInQuestionEnabled && !this.optInQuestion
            ? true
            : this.optInQuestion === this.initialOptInQuestion
    );
    return (noTitleChange && noRefTypeChange && noIsOptInQuestionEnabledChange && noOptInQuestionChange);

    return (noTitleChange && noRefTypeChange);
  }

  isSaveButtonDisabled(): boolean {
    if (this.configureOptInOnly) return this.isOptInQuestionEnabled && this.optInQuestion.length === 0

    return( this.invalidTitle ||
      this.wrongPattern ||
      this.questionnaireTitle.length === 0 ||
      (this.isOptInQuestionEnabled && this.optInQuestion.length === 0) ||
      this.referenceInputError  ||
      this.notesToCandidateValueError ||
      this.notesToRefereeValueError )
  }


  setValidations(){
    this.disableButton = this.isSaveButtonDisabled();
  }

  private mapQuestionCategories(questions: any[]): any[] {
    return questions.map(q => {
      const rawCategory = typeof q.category === 'string' ? q.category.toLowerCase() : q.category;
      const mappedCategory = typeof rawCategory === 'string'
        ? this.category_map[rawCategory] ?? null
        : rawCategory;

      return {
        ...q,
        category: mappedCategory
      };
    });
  }

  decrementReferenceCount() {
    const nextCount = this.minReferee - 1;
    if (nextCount < this.defaultMinRefereeCount) {
      this.referenceDecrementBtnDisabled = true;
      return;
    }
    this.minReferee = nextCount;
    // Clear the max banner (if it was shown earlier)
    this.maxReferenceCountBannerDisplay = this.minReferee === this.defaultMaxRefereeCount; // If we're now at 5, show the banner
    this.referenceDecrementBtnDisabled = (this.minReferee <= this.defaultMinRefereeCount);
    this.referenceIncrementBtnDisabled = (this.minReferee >= this.defaultMaxRefereeCount);
    this.setValidations();
  }


  incrementReferenceCount() {
    const nextCount = this.minReferee + 1;
    if (nextCount > this.defaultMaxRefereeCount) {
      this.referenceIncrementBtnDisabled = true;
      return;
    }
    this.minReferee = nextCount;
    this.maxReferenceCountBannerDisplay = this.minReferee == this.defaultMaxRefereeCount; // Show banner only when minReferee when max (i.e., 5)
    // Update button states
    this.referenceDecrementBtnDisabled = this.minReferee <= this.defaultMinRefereeCount;
    this.referenceIncrementBtnDisabled = this.minReferee >= this.defaultMaxRefereeCount;
    this.setValidations();
  }

  resetReferenceCountErrors() {
    this.referenceDecrementBtnDisabled = false;
    this.referenceIncrementBtnDisabled = false;
    this.referenceAboveExpectedCountError = false;
    this.referenceBelowExpectedCountError = false;
    this.maxReferenceCountBannerDisplay = false;
  }

  updateReferenceCount(referenceCount: number) {

    this.resetReferenceCountErrors();
    if (isNaN(referenceCount)) { //check if reference count is not a number
        this.referenceInputError = true;
        this.setValidations();
        return;
    }
    this.referenceInputError = false;
    if (referenceCount < this.defaultMinRefereeCount) {
        this.referenceBelowExpectedCountError = true;
        this.referenceIncrementBtnDisabled = true;
        this.referenceDecrementBtnDisabled = true;
    } else if (referenceCount > this.defaultMaxRefereeCount) {
        this.referenceAboveExpectedCountError = true;
        this.referenceIncrementBtnDisabled = true;
        this.referenceDecrementBtnDisabled = true;
    } else { //edge case when user enters max/min number
        this.referenceIncrementBtnDisabled = (referenceCount == this.defaultMaxRefereeCount) ? true : false;
        this.referenceDecrementBtnDisabled = (referenceCount == this.defaultMinRefereeCount) ? true : false;
    }
    this.minReferee = referenceCount;
    this.referenceInputError = this.referenceBelowExpectedCountError || this.referenceAboveExpectedCountError;
    this.setValidations();
  }

  noteToCandidateValueChange(updatedNotetoCandidate:string) {
    this.noteToCandidate = updatedNotetoCandidate;
    this.notesToCandidateValueError = this.noteToCandidate.trim() === '';
    this.setValidations();
  }

  noteToRefereeValueChange(updatedNotetoReferee:string) {
    this.noteToReferee = updatedNotetoReferee;
    this.notesToRefereeValueError = this.noteToReferee.trim() === '';
    this.setValidations();
  }
}
